import { forwardRef, InputHTMLAttributes, useState, useEffect } from 'react';
import { BREAKPOINTS } from '../../hooks/is-mobile';

import { cn } from '@web/lib/shadcn';

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  /** Makes the input more touch-friendly on mobile */
  mobileOptimized?: boolean;
};

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, mobileOptimized = true, ...props }, ref) => {
    const [isSmallScreen, setIsSmallScreen] = useState(false);

    useEffect(() => {
      const checkScreenSize = () => {
        setIsSmallScreen(window.innerWidth < BREAKPOINTS.md);
      };

      checkScreenSize();
      window.addEventListener('resize', checkScreenSize);
      return () => {
        window.removeEventListener('resize', checkScreenSize);
      };
    }, []);

    return (
      <input
        type={type}
        className={cn(
          'flex w-full rounded-full border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400',
          'file:border-0 file:bg-transparent file:text-sm file:font-medium',
          'focus:border-primary focus:ring-primary/10 focus:shadow-sm focus:outline-none focus:ring-2',
          'transition-all duration-150',
          'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-50',
          // Conditional height based on screen size for better touch targets
          mobileOptimized && isSmallScreen ? 'h-12' : 'h-10',
          // Slightly larger text on mobile for better readability
          mobileOptimized && isSmallScreen && 'text-base',
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';

export { Input };
